import type { IkeCloneable } from '../const/cloneable';
import type { IkeCollectorType } from '../const/collector';
import type { IkeFieldType } from '../const/field';
import type { IkeProviderType } from '../const/provider';
import type { ParseJSONObject } from './parse';

/**
 * Summary information of a form, this does not contain the field information
 */
export interface FormSummaryJSONObject extends ParseJSONObject {
	/** Unique reference for the entire history of the form */
	uuid: string;
	/**
	 * Version or Revision should be the same value
	 * Some old forms do not have a revision field
	 */
	revision: string;
	/**
	 * Version or revision of the schema, this is the unique reference for all history of a form
	 */
	version: string;
	/** @deprecated not used */
	name: string;
	/** Optional additional information */
	metadata?: FormMetadata;
	/** Name displayed to the user */
	displayname: string;
	/** Version displayed to the user */
	displayversion?: number;
	/** User-provided description */
	description: string;
	/** Whether the form is deleted and should not be listed */
	isDeleted?: boolean;
}
/**
 * Form or Form Schema representation as a JSON Object
 *
 * This provides the definition for what data can be collected.
 *
 * @see @ikegps/core-shared.FormModel
 */
export interface FormJSONObject extends FormSummaryJSONObject {
	/**
	 * Description of the data that can be collected against this schema
	 */
	schema: FormFieldSubFormSchema;
	/** @deprecated Not used */
	parser?: number;
	/** @deprecated Not used */
	v?: number;
	/** userId of the user who last modified the form  */
	updatedBy?: string;
}

export interface FormMetadata {
	import: {
		/**
		 * Internal version of the form.
		 * this is used by importers to determine what version of the importer was used to create the form
		 */
		version: number;
		/**
		 * Type of importer used to create the form, generally 'ikecalc' for our internal importer.
		 */
		type: string;
	};
	/**
	 * Does this form support the workflow status feature
	 */
	status?: FormMetadataStatus;
	/**
	 * Does this form support the map color feature
	 */
	mapColor?: FormMetadataMapColor;
	/**
	 * SpidaCalc export information
	 */
	spidacalc?: FormMetadataSPIDACalc;
	/**
	 * Ocalc export information
	 */
	ocalc?: FormMetadataOCalc;
	/**
	 * DDS export information
	 */
	dds?: FormMetadataDDS;
	/**
	 * Poleforeman export information
	 */
	poleforeman?: FormMetadataPla;
	ikePoleForeman?: FormMetadataIkePoleForeman;
	/**
	 * Quick Pole export information
	 */
	quickpole?: FormMetadataQuickPole;
	/**
	 * Fields to save address information from reverse address lookup
	 */
	address_lookup?: FormMetadataAddressLookup;
}

/**
 *
 */
export interface FormMetadataStatus {
	/** The nested list to use for the "status" of the collection */
	field: string;
}

export interface FormMetadataMapColor {
	/** The nested or select list to use for the "map color" of the collection */
	field: string;
}

/** Metadata for geocoding fields */
export interface FormMetadataAddressLookup {
	address: {
		field: string;
	};
	city?: {
		field: string;
	};
	state?: {
		field: string;
	};
}

/** Metadata for pole loading exports */
export interface FormMetadataPla {
	/**
	 *  attachmentId of the PLA configuration used to create the form
	 * lccfg for Ocalc, clientFile for SpidaCalc
	 */
	config?: string;
}

/** Metadata export options for SPIDACalc */
export interface FormMetadataSPIDACalcExportOptions {
	/**
	 * Should images be exported
	 * @default true
	 */
	useImages: boolean;
	/**
	 * Should WEP environments be exported
	 * @default true
	 */
	useEnvironment: boolean;
	/**
	 * Should Wire incline be exported
	 * @default true
	 */
	useIncline: boolean;
	/**
	 * Should anchor height be exported from the vertical component of anchor length @see IKE4-2544
	 * @default false
	 */
	useAnchorHeightVector: boolean;
}

/**
 * Form metadata specific to SpidaCalc exports,
 *
 * More information about how the export process works can be found in the SpidaCalc exporter.
 *
 * @see @ikegps/core-shared/export
 */
export interface FormMetadataSPIDACalc extends FormMetadataPla, Partial<FormMetadataSPIDACalcExportOptions> {
	/** Wire information as needed in export */
	wires?: (string | number)[][];

	/** Name of the client file that generated the form */
	clientfile?: string;
}

export interface FormMetadataOCalc extends FormMetadataPla {
	/** @deprecated */
	configurationfile?: string;
	/** @deprecated */
	file?: {
		id: string;
		name: string;
	};
}

export interface FormMetadataDDS extends FormMetadataPla {}

export interface FormMetadataQuickPole extends FormMetadataPla {
	metric?: boolean;
	codebase?: string;
}

export interface FormMetadataIkePoleForeman extends FormMetadataPla {
	configFormat?: 'db_portal_encrypted' | 'db_file_unencrypted';
	formVersion?: 1 | 2 | 3;
	accessOrgId?: string;
}

export interface IkeProviderCustom {
	type: IkeProviderType;
}

export interface BaseFormField {
	/**
	 * Unique reference to the field.
	 *
	 * This is how all data is linked against the field in the collection
	 *
	 * This should be unique across the entire Form, however sometimes this is not the case
	 * This will be fixed with the form normalization work.
	 */
	name: string;

	/**
	 * Name of the field shown to the user.
	 */
	displayname: string;

	/**
	 * Type of the field, eg: 'text' or 'location'.
	 */
	type: IkeFieldType;

	/**
	 * Whether this field should be used as the title field for the collection.
	 * (default: false)
	 * @deprecated 2018-03-07 Use FormFieldSubFormSchema.label instead
	 */
	title?: boolean;

	/**
	 * Whether this field should be used as the subtitle field for the collection.
	 * Subtitle means that the collections display title in ike office will show the
	 * subtitle field’s value until the title field’s value is populated
	 * (default: false)
	 */
	subtitle?: boolean;

	/**
	 * Whether this field can be updated by admin users only.
	 * When a field is set to adminOnly, it is automatically set to be automatically hidden in IKE Field
	 * (default: false)
	 */
	adminOnly?: boolean;

	/**
	 * The id of a field that will be used for label titles in photomeasure page
	 * If this is null or undefined, the label should use the parent title (the title of the subform which is set by subtitle or title fields)
	 */
	annotationLabelFieldId?: string;

	/**
	 * the clearance threshold in meters for vertical separation fields.
	 * if this is null or undefined, no clearance checks should be made.
	 */
	clearanceThresholdInMeters?: number;

	/**
	 * Prefered place to collect the field.
	 * (default: all)
	 */
	collector?: IkeCollectorType;

	/**
	 * Cloning strategy to use with this field.
	 *
	 * Options:
	 * 'all' - All field values
	 * 'first' - Only the first field value
	 * 'none' - None of the field values
	 *
	 * (default: 'none')
	 */
	cloneable?: IkeCloneable;

	/**
	 * Providers that can be used with this field.
	 *
	 * Options:
	 * 'existingspan' - Allow Span linking
	 */
	providers?: (IkeProviderType | IkeProviderCustom)[];
	/**
	 * required fields in Ike Field
	 * (default: false, unless it's a photo field)
	 */
	required?: boolean;

	/**
	 * Whether this field is locked and cannot be edited by users.
	 * (default: false)
	 */
	locked?: boolean;

	/**
	 * Whether this field is was generated by ike office (not created by a user)
	 * !! Does not apply to fields generated when creating Base Form. !!
	 * (default: false)
	 */
	isGenerated?: boolean;
}

export interface FormFieldSubForm extends BaseFormField {
	type: 'form';
	schema: FormFieldSubFormSchema;
}

export enum SubFormType {
	NONE = 'None',
	POLE = 'pole',
	SPAN = 'span',
	ANCHOR = 'anchor',
	EQUIPMENT = 'equipment',
}

/**
 * PLA (Pole Load Analysis) type — the external system a "PLA form" integrates with.
 *
 * This is distinct from {@link SubFormType}, which describes a form's physical-entity
 * shape (pole/span/anchor/equipment). A base (non-PLA) form has no PlaType.
 */
export enum PlaType {
	SPIDACALC = 'spidacalc',
	OCALC = 'ocalc',
	POLEFOREMAN = 'poleforeman',
	IPF = 'ipf',
	DDS = 'dds',
	QUICKPOLE = 'quickpole',
}

/**
 * Raw Form Schema
 *
 * List of fields that can be collected in a Collection
 */
export interface FormFieldSubFormSchema {
	/** Ordered array of label names for constructing sub-form title */
	label?: string[];
	/**
	 * List of possible fields that can be used in the sub form
	 */
	fields: FormField[];

	/**
	 * Subform Type (can be undefined for backwards compatability, then set to null or span)
	 */
	subformType?: SubFormType | null;

	/**
	 * State for Span Subforms; only defined if subformType is 'span'.
	 */
	subformSpanTypeState?: SubFormSpanTypeState | null;

	/**
	 * State for Span Subforms; only defined if subformType is 'span'.
	 */
	subformAnchorTypeState?: SubFormAnchorTypeState | null;

	/**
	 * State for Span Subforms; only defined if subformType is 'span'.
	 */
	subformEquipmentTypeState?: SubFormEquipmentTypeState | null;
}

/**
 * State that is only included if the SubForm type is set to SPAN.
 * Defines the field links for the Span (vector, span type, and optional mid span photo fields).
 */
export type SubFormSpanTypeState = {
	vectorFieldId: string;
	typeFieldId: string;
	environmentFieldId?: string;
	midSpanIkePhotoFieldId?: string;
};

/**
 * State that is only included if the SubForm type is set to ANCHOR.
 * Defines the field links for the Anchor (lead length and owner fields).
 */
export type SubFormAnchorTypeState = {
	leadLengthFieldId: string;
	ownerFieldId: string;
};
/**
 * State that is only included if the SubForm type is set to EQUIPMENT.
 * Defines the field links for the Anchor (type, orientation, owner, attachment height and optional bottom height).
 */
export type SubFormEquipmentTypeState = {
	typeFieldId: string;
	orientationFieldId: string;
	ownerFieldId: string;
	attachmentHeightFieldId: string;
	bottomHeightFieldId?: string;
};

/** Possible field types */
export type FormField =
	| BaseFormField
	| FormFieldNestedList
	| FormFieldSelectList
	| FormFieldSubForm
	| FormFieldLine
	| FormFieldAssetType;

export function isFormFieldNestedList(field: FormField): field is FormFieldNestedList {
	return field.type === 'nestedlist';
}

export function isFormFieldSelectList(field: FormField): field is FormFieldSelectList {
	return field.type === 'selectlist';
}

export function isFormFieldSubForm(field: FormField): field is FormFieldSubForm {
	return field.type === 'form';
}

export function isFormFieldLine(field: FormField): field is FormFieldLine {
	return field.type === 'line';
}

export function isFormFieldAssetType(field: FormField): field is FormFieldAssetType {
	return field.type === 'assettype';
}

export interface FormFieldNestedList extends BaseFormField {
	type: 'nestedlist';
	options: FormFieldNestedListOptions;
}

export interface FormFieldNestedListValues {
	/**
	 * Value to use for this option.
	 */
	value: string;
	/**
	 * Optional more nested options
	 */
	options?: FormFieldNestedListOptions;

	/** Unique code for the value */
	code?: string;

	/**
	 * Color to represent the value
	 * HTML RGB (#ff00ff)
	 */
	color?: string;

	/** Icon to use to represent the value @see MaterialIcons*/
	icon?: string;
}

export interface FormFieldNestedListOptions {
	/**
	 * Title for this level of options
	 */
	title: string;
	/**
	 * Options for this level.
	 */
	values: FormFieldNestedListValues[];
}

export interface FormFieldSelectList extends BaseFormField {
	type: 'selectlist';
	/**
	 * List of possible selectable options.
	 */
	options: FormFieldSelectListOptions[];

	// TODO remove this once confirmed mobile doesnt use it!
	value: string;
}

export interface FormFieldSelectListOptions {
	/**
	 * Display value to show the user.
	 */
	text: string;
	/**
	 * Internal value to use for this option.
	 */
	value: string;
	/**
	 * Color to represent the value
	 * HTML RGB (#ff00ff)
	 */
	color?: string;
}

export interface FormFieldLine extends BaseFormField {
	type: 'line';
	lineTypes: FormFieldLineTypeOptions[];
}

export interface FormFieldLineTypeOptions {
	/** Shape id — the base stroke/marker/label rendering (NOT a per-row identity). */
	symbolId: string;
	/** Color for this line type (HTML hex); the shape carries no color. */
	symbolColor: string;
	/**
	 * Display title — editable by the designer; the stable identity a collected line stores.
	 * MUST be unique and non-empty within a field's `lineTypes` (title-match resolution + preset detection).
	 */
	title: string;
	/** Whether this type is offered in the form. */
	visible: boolean;
}

export interface FormFieldAssetType extends BaseFormField {
	type: 'assettype';
	assetTypes: FormFieldAssetTypeOptions[];
}

export interface FormFieldAssetTypeOptions {
	/** Material Icons font ligature (e.g. `light_mode`). @see MaterialIcons */
	icon: string;
	/** Color for this asset type (HTML hex). */
	color: string;
	/**
	 * Display title — editable by the designer; the stable identity a collected asset type reference stores.
	 * MUST be unique and non-empty within a field's `assetTypes` (title-match resolution + preset detection).
	 */
	title: string;
	/** Whether this type is offered in the form. */
	visible: boolean;
}
